import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ListMusic, Play, Plus, X, BookOpen, ClipboardCheck } from "lucide-react";

const KEY = "chemlab_playlist";

const loadList = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const saveList = (list) => localStorage.setItem(KEY, JSON.stringify(list));

export default function Playlist() {
  const [topics, setTopics] = useState([]);
  const [items, setItems] = useState(loadList());

  useEffect(() => { api.get("/topics").then((r) => setTopics(r.data.topics)).catch(() => {}); }, []);

  const add = (kind, topicId) => {
    if (items.some((x) => x.kind === kind && x.topic_id === topicId)) return;
    const next = [...items, { kind, topic_id: topicId, id: `${kind}-${topicId}-${Date.now()}` }];
    setItems(next); saveList(next);
  };
  const remove = (i) => { const n = items.filter((_, idx) => idx !== i); setItems(n); saveList(n); };
  const clear = () => { setItems([]); saveList([]); };

  const topicMap = Object.fromEntries(topics.map((t) => [t.id, t]));

  return (
    <div className="space-y-8" data-testid="playlist-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 text-xs uppercase tracking-widest text-fuchsia-300">
          <ListMusic size={12} /> Study Playlist
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Queue up a study session.</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">Mix topics and quizzes into an order you'll actually finish. Saved on this device.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Catalog */}
        <div>
          <h2 className="font-display text-xl font-semibold">Add to your playlist</h2>
          <div className="mt-4 space-y-3">
            {topics.map((t) => (
              <div key={t.id} className="rounded-md border border-white/10 bg-black/40 p-4" data-testid={`catalog-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-white">{t.title}</div>
                    <div className="text-xs text-slate-500">{t.difficulty} · {t.minutes} min</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => add("topic", t.id)} data-testid={`add-topic-${t.id}`} className="inline-flex items-center gap-1 rounded-md border border-cyan-400/40 bg-cyan-500/10 px-2 py-1 text-xs text-cyan-200 hover:bg-cyan-500/15">
                      <Plus size={12} /> Lesson
                    </button>
                    <button onClick={() => add("quiz", t.id)} data-testid={`add-quiz-${t.id}`} className="inline-flex items-center gap-1 rounded-md border border-lime-400/40 bg-lime-500/10 px-2 py-1 text-xs text-lime-200 hover:bg-lime-500/15">
                      <Plus size={12} /> Quiz
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Playlist */}
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold">Your queue ({items.length})</h2>
            {items.length > 0 && (
              <button onClick={clear} className="text-xs text-slate-400 hover:text-red-300" data-testid="clear-playlist">Clear all</button>
            )}
          </div>
          <div className="mt-4 space-y-2" data-testid="queue">
            {items.length === 0 && (
              <div className="rounded-md border border-dashed border-white/10 bg-black/20 p-8 text-center text-sm text-slate-500">
                Nothing queued yet. Add a topic or quiz from the left.
              </div>
            )}
            {items.map((it, i) => {
              const t = topicMap[it.topic_id];
              const Icon = it.kind === "topic" ? BookOpen : it.kind === "lesson" ? BookOpen : ClipboardCheck;
              const to = it.kind === "topic" ? `/topics/${it.topic_id}` : it.kind === "lesson" ? `/curriculum/${it.year}/${it.order}` : `/quiz/${it.topic_id}`;
              const label = it.kind === "lesson" ? (it.title || `Lesson Y${it.year}#${it.order}`) : (t?.title || it.topic_id);
              const color = it.kind === "topic" ? "text-cyan-300" : it.kind === "lesson" ? "text-fuchsia-300" : "text-lime-300";
              return (
                <div key={it.id} className="flex items-center justify-between rounded-md border border-white/10 bg-black/40 p-3" data-testid={`queue-item-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="font-mono-num w-6 text-center text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</div>
                    <Icon size={16} className={color} />
                    <div>
                      <div className="text-sm text-white">{label}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{it.kind}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={to} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs hover:bg-white/10"><Play size={12} className="inline" /></Link>
                    <button onClick={() => remove(i)} className="rounded-md border border-white/10 bg-white/5 p-1.5 text-slate-400 hover:text-red-300"><X size={12} /></button>
                  </div>
                </div>
              );
            })}
          </div>
          {items.length > 0 && (
            <Link
              to={items[0].kind === "topic" ? `/topics/${items[0].topic_id}` : items[0].kind === "lesson" ? `/curriculum/${items[0].year}/${items[0].order}` : `/quiz/${items[0].topic_id}`}
              data-testid="start-session"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-fuchsia-500 px-4 py-3 text-sm font-semibold text-white hover:bg-fuchsia-400"
            >
              <Play size={14} /> Start session
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
